<script setup lang="ts">
  import { computed } from 'vue';
  import { IconRadio, PositionSelector } from '@/shared/ui';
  import type { RecordConfigOption } from '../../api/record-config-options.api';
  import { SUBJECT_MODE_OPTIONS } from '../../lib/record-config-options';
  import type { SubjectSelection, SubjectSelectionMode } from '../../model/record-config.types';

  const SELECTOR_HEIGHT_CLASS = 'h-[min(27rem,38vh)]';

  const props = defineProps<{
    title: string;
    helperText: string;
    radioName: string;
    automaticOnly: boolean;
    modelValue: SubjectSelection;
    positions: RecordConfigOption[];
    functionalActivities: RecordConfigOption[];
    isLoading: boolean;
    hasError: boolean;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: SubjectSelection];
  }>();

  const options = SUBJECT_MODE_OPTIONS.filter(({ id }) => id !== 'AUTOMATIC');
  const AUTOMATIC_LABEL = SUBJECT_MODE_OPTIONS.find(({ id }) => id === 'AUTOMATIC')?.name ?? '';

  // `IconRadio` uses `value` as the DOM id, so the radio name prefixes it to keep two sections
  // on the same page from sharing ids.
  const radioValue = (mode: SubjectSelectionMode): string => `${props.radioName}-${mode}`;

  const selected = computed(() => radioValue(props.modelValue.mode));
  const showsSelectors = computed(
    () => !props.automaticOnly && props.modelValue.mode === 'SELECT_SUBJECTS'
  );

  const selectedPositionIds = computed(() => props.modelValue.positionIds.map(String));
  const selectedFunctionalActivityIds = computed(() =>
    props.modelValue.functionalActivityIds.map(String)
  );

  function selectMode(value: string | undefined): void {
    const option = options.find(({ id }) => radioValue(id) === value);
    if (!option) return;
    emit('update:modelValue', {
      mode: option.id,
      positionIds: option.id === 'SELECT_SUBJECTS' ? props.modelValue.positionIds : [],
      functionalActivityIds:
        option.id === 'SELECT_SUBJECTS' ? props.modelValue.functionalActivityIds : [],
    });
  }

  function updatePositionIds(ids: string[]): void {
    emit('update:modelValue', { ...props.modelValue, positionIds: ids.map(Number) });
  }

  function updateFunctionalActivityIds(ids: string[]): void {
    emit('update:modelValue', { ...props.modelValue, functionalActivityIds: ids.map(Number) });
  }
</script>

<template>
  <section class="flex flex-col gap-3 px-8 py-6" data-testid="record-config-subjects-section">
    <span class="text-sm font-semibold text-gray-07">{{ title }}</span>
    <div v-if="automaticOnly" class="flex flex-wrap gap-x-8 gap-y-3">
      <IconRadio
        :model-value="radioValue('AUTOMATIC')"
        :value="radioValue('AUTOMATIC')"
        :label="AUTOMATIC_LABEL"
        :name="radioName"
        variant="medium"
        disabled
        data-testid="record-config-subjects-automatic"
      />
    </div>
    <div v-else class="flex flex-wrap gap-x-8 gap-y-3">
      <IconRadio
        v-for="option in options"
        :key="option.id"
        :model-value="selected"
        :value="radioValue(option.id)"
        :label="option.name"
        :name="radioName"
        variant="medium"
        @update:model-value="selectMode"
      />
    </div>

    <template v-if="showsSelectors">
      <p
        class="mt-3 text-sm font-semibold text-gray-07"
        data-testid="record-config-subjects-helper"
      >
        {{ helperText }}
      </p>
      <div class="flex gap-8">
        <PositionSelector
          mode="positions"
          :height-class="SELECTOR_HEIGHT_CLASS"
          :items="positions"
          :is-loading="isLoading"
          :has-error="hasError"
          :selected-ids="selectedPositionIds"
          @update:selected-ids="updatePositionIds"
        />
        <PositionSelector
          mode="functionalActivities"
          :height-class="SELECTOR_HEIGHT_CLASS"
          :items="functionalActivities"
          :is-loading="isLoading"
          :has-error="hasError"
          :selected-ids="selectedFunctionalActivityIds"
          @update:selected-ids="updateFunctionalActivityIds"
        />
      </div>
    </template>
  </section>
</template>
